import { Box, Tooltip } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { compact } from 'lodash';
import { useMemo } from 'react';

import { JobTagIcon } from './job-tag-icon';

/** The shape every display surface gets from the job's `tags` relation. */
export interface JobTagDisplay {
  _id: string;
  name: string;
  icon: string;
  sortOrder?: number | null;
}

interface JobTagIconsProps {
  tags: readonly (JobTagDisplay | null | undefined)[] | null | undefined;
  /**
   * How many icons to draw before collapsing the rest into a `+N`. A job with
   * a dozen tags must not push a calendar chip's title or a table's name
   * column around.
   */
  max?: number;
  fontSize?: 'inherit' | 'small' | 'medium' | 'large';
  /** Pixel size for the icons; overrides `fontSize` when given. */
  size?: number;
  /** MUI spacing units between icons. Match the surrounding rail. */
  gap?: number;
  /**
   * Merged over the wrapper's defaults. Pass `{ display: 'contents' }` to
   * dissolve the wrapper so each icon becomes a direct child of a parent
   * grid instead of all of them sharing one cell.
   */
  sx?: SxProps<Theme>;
}

/**
 * A compact row of job-tag icons, each with the tag's name on hover, and a
 * `+N` carrying the names that didn't fit.
 *
 * Icons only, by design: this is the dense surface (calendar chip, table
 * cell). The full icon-plus-name treatment lives on the job page header and
 * the calendar tooltip.
 * @param root0
 * @param root0.tags
 * @param root0.max
 * @param root0.fontSize
 * @param root0.size
 */
export function JobTagIcons({
  tags,
  max = 3,
  fontSize = 'small',
  size = undefined,
  gap = 0.25,
  sx = undefined,
}: JobTagIconsProps) {
  // The job's own order — the order the tags were put on. Every surface
  // draws them this way, so the icons here are the first chips elsewhere.
  const ordered = useMemo(() => compact(tags), [tags]);

  if (ordered.length === 0) return null;

  const shown = ordered.slice(0, max);
  const hidden = ordered.slice(max);
  const sizeSx = size ? { fontSize: size } : undefined;

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap,
        flexShrink: 0,
        verticalAlign: 'middle',
        ...sx,
      }}
    >
      {shown.map((tag) => (
        <Tooltip key={tag._id} title={tag.name}>
          {/* The icon is the tooltip's only child and needs a focusable,
              ref-able wrapper for hover to register reliably inside the
              calendar's re-rendered chips. */}
          <Box component="span" sx={{ display: 'inline-flex' }}>
            <JobTagIcon
              iconKey={tag.icon}
              fontSize={fontSize}
              sx={sizeSx}
              titleAccess={tag.name}
            />
          </Box>
        </Tooltip>
      ))}
      {hidden.length > 0 && (
        <Tooltip title={hidden.map((t) => t.name).join(', ')}>
          <Box
            component="span"
            sx={{
              fontSize: size ? size * 0.75 : '0.7rem',
              lineHeight: 1,
              fontWeight: 600,
              opacity: 0.8,
            }}
          >
            +{hidden.length}
          </Box>
        </Tooltip>
      )}
    </Box>
  );
}
